import { terminalKind, terminalReplyIsNew } from "./terminalReplyPolicy.mjs";
const LEAD_TERMINAL_STAGES = new Set(["prospeccao_futura"]);

export function currentDateKeySaoPaulo(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function leadCanAwaitTeamReply(lead, todayKey) {
  if (!lead || LEAD_TERMINAL_STAGES.has(lead.estagio)) return false;
  return true;
}

export function replyTimestamp(value) {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    if (value >= 1e15) return Math.floor(value / 1000);
    if (value > 0 && value < 1e11) return value * 1000;
    return value;
  }
  const numeric = typeof value === "string" && /^\d+$/.test(value.trim())
    ? Number(value.trim())
    : null;
  if (numeric != null) return replyTimestamp(numeric);
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function isConfirmedAutomatedFollowUp(message) {
  if (!message || !(message.commercialFollowUp === true || message.messageSource === "commercial-followup"
    || message.remetente === "automacao_comercial")) return null;
  if (["pending", "sending", "retry", "failed", "uncertain"].includes(message.status)) return 0;
  const acceptedAck = ["SENT", "RECEIVED", "DELIVERED", "READ", "PLAYED"]
    .includes(String(message.waAck || "").toUpperCase());
  const confirmed = message.status === "sent" || message.provider_accepted === true
    || Boolean(message.providerSendId || message.providerMessageId)
    || acceptedAck;
  // Historical sends without sentAt can still carry a provider receipt time.
  // Enqueue/updatedAt is not confirmation and must never order this message.
  const at = replyTimestamp(message.confirmedAt || message.sentAt
    || (acceptedAck && message.waAckAt));
  return confirmed && at > 0 ? at : 0;
}

export function conversationActionTimestamp(message) {
  if(message?.messageSource==="firebase_outage_gap_note")return 0;
  if(message?.remetente!=="cliente"&&message?.direction!=="inbound"
    &&["pending","sending","retry","failed","uncertain"].includes(message?.status))return 0;
  const followUpConfirmation = isConfirmedAutomatedFollowUp(message);
  if (followUpConfirmation !== null) return followUpConfirmation;
  const outboundConfirmed = message?.remetente !== "cliente"
    && message?.direction !== "inbound"
    && (
      message?.status === "sent"
      || message?.provider_accepted === true
      || message?.providerSendId
      || message?.providerMessageId
      || ["SENT", "RECEIVED", "DELIVERED", "READ", "PLAYED"].includes(
        String(message?.waAck || "").toUpperCase(),
      )
    );
  return replyTimestamp(outboundConfirmed
    ? (message?.confirmedAt || message?.sentAt || message?.waAckAt
      || message?.timestamp || message?.createdAt)
    : (message?.timestamp || message?.createdAt));
}

export function latestConversationMessage(messages = []) {
  return (messages || []).reduce((latest, message) => (
    conversationActionTimestamp(message) > 0
      && conversationActionTimestamp(message) > conversationActionTimestamp(latest) ? message : latest
  ), null);
}

export function conversationReplyState(messages = []) {
  const lastMessage = latestConversationMessage(messages);
  if (!lastMessage) return "no_messages";
  if (lastMessage.remetente === "nota_interna") return "treated";
  if (lastMessage.remetente !== "cliente") return "awaiting_customer";
  return "needs_team_reply";
}

export function postSaleReplyState(record, messages = []) {
  const last=latestConversationMessage(messages);
  if(!last||last.remetente!=="cliente")return "not_actionable";
  return terminalReplyIsNew(record,new Date(conversationActionTimestamp(last)).toISOString())
    ? "needs_team_reply" : "not_actionable";
}

export function auroraHandoffAwaitsConsultant(lead, messages = []) {
  if (terminalKind(lead)) return false;
  if (!leadCanAwaitTeamReply(lead)) return false;
  const hasHandoff = lead?.aurora_handoff === true
    || lead?.aurora_handoff === 1
    || String(lead?.aurora_handoff).toLowerCase() === "true";
  if (!hasHandoff) return false;

  const handoffAt = replyTimestamp(lead?.aurora_handoff_at);
  return !(messages || []).some(message => (
    (message?.remetente === "consultor" || message?.remetente === "nota_interna"
      || (message?.remetente !== "cliente"
        && message?.direction !== "inbound"
        && !["pending", "sending", "retry", "failed", "uncertain"].includes(message?.status)
        && (message?.status === "sent" || message?.provider_accepted === true
          || message?.providerSendId || message?.providerMessageId
          || ["SENT", "RECEIVED", "DELIVERED", "READ", "PLAYED"].includes(
            String(message?.waAck || "").toUpperCase(),
          ))))
    && (!handoffAt || conversationActionTimestamp(message) > handoffAt)
  ));
}

export function leadConversationReplyState(lead, messages, {
  hasHumanOwnership = true,
} = {}) {
  if(terminalKind(lead)){
    return hasHumanOwnership?postSaleReplyState(lead,messages):"automated_service";
  }
  if (!leadCanAwaitTeamReply(lead)) return "not_actionable";
  if (!hasHumanOwnership) return "automated_service";
  const lastMessage = latestConversationMessage(messages);
  if (!lastMessage) return "no_messages";

  const manualWaiting = Boolean(
    lead?.aguardandoRetornoCliente?.value
    && conversationActionTimestamp(lastMessage) <= replyTimestamp(lead.aguardandoRetornoCliente.setAt)
  );
  if (manualWaiting) return "awaiting_customer_manual";
  if (lastMessage.remetente === "nota_interna") return "treated";
  if (lastMessage.remetente !== "cliente") return "awaiting_customer";
  return "needs_team_reply";
}